import { axiosPatientInstance } from "config";
import { EncounterIntegrationAYVAProtocol, IntegrationStatus } from "enums";
import { useAxiosMutation } from "hooks";
import { useQueryClient } from "react-query";
import { useTrackSharedSectionChanges } from "modules/charts-shared/hooks/useTrackSharedSectionChanges";

type AYVAResponse = {
  sentRequestInd: boolean;
  sentAYVAPatientInd: boolean;
  sentAYVAEncounterInd: boolean;

  messageInfoTitle?: string;
  messageInfoAction?: string;
  messageInfoMessages?: string[];
};

function useUpdateEncounterIntegrationAYVA(encounterId: string) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    ["updateEncounterIntegrationAYVA", { encounterId }],
    () =>
      axiosPatientInstance.post<AYVAResponse>(`v1/integration/ayva/`, {
        encounterId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "encounterIntegrations",
          { encounterId },
        ]);
      },
    }
  );
}

function useUpdateEncounterIntegrationAYVABMI(
  encounterId: string,
  protocol: EncounterIntegrationAYVAProtocol
) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    ["updateEncounterIntegrationAYVABMI", { encounterId, protocol }],
    () =>
      axiosPatientInstance.post<AYVAResponse>(`v1/integration/ayva/bmi/`, {
        encounterId,
        protocol,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "encounterIntegrations",
          { encounterId },
        ]);
      },
    }
  );
}

function useUpdateEncounterIntegrationAYVAPharmacy(
  encounterId: string,
  protocol: EncounterIntegrationAYVAProtocol
) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    ["updateEncounterIntegrationAYVAPharmacy", { encounterId, protocol }],
    (successStatus: IntegrationStatus) =>
      axiosPatientInstance.post<AYVAResponse>(`v1/integration/ayva/pharmacy/`, {
        encounterId,
        protocol,
        successStatus,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "encounterIntegrations",
          { encounterId },
        ]);
      },
    }
  );
}

type AYVAAllergiesResponse = {
  sentRequestInd: boolean;
  sentAllergiesMedicationsRxInd: boolean;
  erroredAllergiesMedicationsRxCount: number;
  ignoredAllergiesMedicationsRxCount: number;
  completeAllergiesMedicationsRxCount: number;

  messageInfoTitle?: string;
  messageInfoAction?: string;
  messageInfoMessages?: string[];
};

function useUpdateEncounterIntegrationAYVAAllergies(encounterId: string) {
  const queryClient = useQueryClient();
  const { trackChanges } = useTrackSharedSectionChanges(encounterId, "SAMRX");

  return useAxiosMutation(
    ["updateEncounterIntegrationAYVAAllergies", { encounterId }],
    () =>
      axiosPatientInstance.post<AYVAAllergiesResponse>(
        `v1/integration/ayva/allergies-medications-rx/`,
        {
          encounterId,
        }
      ),
    {
      onSuccess: async (response) => {
        const result = response.data;
        const errors = result.erroredAllergiesMedicationsRxCount ?? 0;
        const outcome = !result.sentRequestInd
          ? "Send failed"
          : result.messageInfoTitle ||
            (errors > 0
              ? `Send completed with ${errors} errors`
              : result.sentAllergiesMedicationsRxInd
              ? "Information sent"
              : "No changes sent");
        await trackChanges([
          {
            code: "AMRX.AYVA",
            label: "AYVA Allergies & Medications",
            previousValue: "",
            value: outcome,
          },
        ]);
        queryClient.invalidateQueries([
          "encounterIntegrations",
          { encounterId },
        ]);
        queryClient.invalidateQueries([
          "encounterAYVAAllergyIntegration",
          { encounterId },
        ]);
      },
      onError: async () => {
        await trackChanges([
          {
            code: "AMRX.AYVA",
            label: "AYVA Allergies & Medications",
            previousValue: "",
            value: "Send failed",
          },
        ]);
      },
    }
  );
}

export type { AYVAResponse, AYVAAllergiesResponse };
export {
  useUpdateEncounterIntegrationAYVA,
  useUpdateEncounterIntegrationAYVABMI,
  useUpdateEncounterIntegrationAYVAPharmacy,
  useUpdateEncounterIntegrationAYVAAllergies,
};
